import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { getEmail } from "@/lib/pro";
import { hasGavel } from "@/lib/playerSync";
import { getChips, spendChips, addChips } from "@/lib/chips";
import {
  HANDLE_GEMS,
  PRISMATIC_GEM,
  getGemInventory,
  consumeGem,
  addGem,
  getHandleGem,
} from "@/lib/achievementsExtra";
import { sfx } from "@/lib/sfx";
import { toggleWatch, isWatched, watchedCount, maxWatch } from "@/lib/auctionWatchlist";
import { getBidbotCeiling, setBidbotCeiling, nextAutoBidTargets } from "@/lib/bidbot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_GEMS = [...HANDLE_GEMS, PRISMATIC_GEM];
const gemMeta = (id) => ALL_GEMS.find((g) => g.id === id);

// Prismatic-gem holders enjoy a 15% discount on all buy prices.
const PRISMATIC_DISCOUNT = 0.15;
function discountedPrice(price) {
  return getHandleGem() === "prismatic"
    ? Math.max(1, Math.ceil(price * (1 - PRISMATIC_DISCOUNT)))
    : price;
}

export default function TradeHallDialog({ open, onOpenChange }) {
  const [listings, setListings] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inventory, setInventory] = useState({});
  const [chips, setChipsState] = useState(0);
  const [topTrader, setTopTrader] = useState(null);
  const [sellGem, setSellGem] = useState("");
  const [sellPrice, setSellPrice] = useState("100");
  const [sellMode, setSellMode] = useState("listing"); // "listing" | "auction"
  const [busyId, setBusyId] = useState("");
  const [bidAmounts, setBidAmounts] = useState({}); // { auctionId: amount }
  const [reactions, setReactions] = useState({});   // { auctionId: { fire, eyes, trophy } }
  const [reactBusy, setReactBusy] = useState(false);
  const [gavelRoom, setGavelRoom] = useState(false); // GAVEL-only filter for whale auctions

  const email = getEmail();
  const gavelHolder = hasGavel();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [lRes, tRes, aRes] = await Promise.all([
        axios.get(`${API}/gems/listings?limit=30`),
        axios.get(`${API}/gems/top-trader`),
        axios.get(`${API}/gems/auctions?limit=20`),
      ]);
      setListings(lRes.data?.listings || []);
      setTopTrader(tRes.data?.top || null);
      const auc = aRes.data?.auctions || [];
      setAuctions(auc);
      // Fetch reactions for all visible auctions
      if (auc.length) {
        try {
          const rr = await axios.get(`${API}/gems/auctions/reactions?auction_ids=${auc.map((a) => a.id).join(",")}`);
          setReactions(rr.data?.reactions || {});
        } catch { setReactions({}); }
      } else {
        setReactions({});
      }
      // Bid Bot auto-bid pass
      if (email) {
        const handleLower = (localStorage.getItem("ppe_public_handle_v1") || "").toLowerCase();
        const targets = nextAutoBidTargets(auc, email, handleLower);
        for (const { auction, minNext } of targets) {
          const spend = spendChips(minNext);
          if (!spend.ok) continue;
          try {
            const r = await axios.post(`${API}/gems/auctions/bid`, {
              bidder_email: email, auction_id: auction.id, amount: minNext,
            });
            toast.success(`BIDBOT · +${minNext} on ${auction.gem?.toUpperCase()}${r.data?.sniped ? " · SNIPED" : ""}`);
          } catch {
            addChips(minNext);
          }
        }
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
      setInventory(getGemInventory());
      setChipsState(getChips());
    }
  }, [email]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  // Consume a "FLIP hint" set by the Hall of Fame → auto-fill sell form as AUCTION
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem("ppe_flip_hint_v1");
      if (raw) {
        const hint = JSON.parse(raw);
        localStorage.removeItem("ppe_flip_hint_v1");
        if (hint?.gem && hint?.price) {
          setSellGem(hint.gem);
          setSellPrice(String(hint.price));
          setSellMode("auction");
          toast.info(`FLIP AUCTION · ${gemMeta(hint.gem)?.symbol} · start bid ${hint.price} · 24h`);
        }
      }
    } catch {}
  }, [open]);

  const canList = email && sellGem && (inventory[sellGem] || 0) > 0 && parseInt(sellPrice, 10) >= 10;

  const submitListing = async () => {
    if (!canList) return;
    const price = parseInt(sellPrice, 10) || 0;
    // Preflight: consume gem locally first (server won't check inventory).
    if (!consumeGem(sellGem)) {
      toast.error("You no longer own that gem");
      setInventory(getGemInventory());
      return;
    }
    try {
      if (sellMode === "auction") {
        await axios.post(`${API}/gems/auctions/create`, {
          seller_email: email,
          gem: sellGem,
          start_bid: price,
        });
        sfx.click();
        toast.success(`AUCTION LISTED · ${gemMeta(sellGem)?.symbol} · start ${price} chips · 24h`);
      } else {
        await axios.post(`${API}/gems/list`, {
          seller_email: email,
          gem: sellGem,
          price,
        });
        sfx.click();
        toast.success(`LISTED · ${gemMeta(sellGem)?.symbol} ${gemMeta(sellGem)?.name} for ${price} chips`);
      }
      setSellGem("");
      setSellPrice("100");
      setSellMode("listing");
      refresh();
    } catch (e) {
      // Rollback local inventory on failure
      addGem(sellGem);
      const msg = e?.response?.data?.detail || "Listing failed";
      toast.error(msg.toUpperCase());
      setInventory(getGemInventory());
    }
  };

  const buyListing = async (listing) => {
    if (!email) {
      toast.error("Sign in to trade — set your handle first");
      return;
    }
    const effective = discountedPrice(listing.price);
    if (chips < effective) {
      toast.error(`Need ${effective - chips} more chips`);
      return;
    }
    setBusyId(listing.id);
    // Preflight: deduct chips locally so we don't double-spend on refresh.
    const spend = spendChips(effective);
    if (!spend.ok) {
      toast.error("Not enough chips");
      setBusyId("");
      return;
    }
    try {
      const effOrig = discountedPrice(listing.price);
      const discountSaved = Math.max(0, listing.price - effOrig);
      await axios.post(`${API}/gems/buy`, {
        buyer_email: email,
        listing_id: listing.id,
        discount_saved: discountSaved,
      });
      addGem(listing.gem);
      sfx.click();
      const discountNote = effective < listing.price ? ` (◈ -${listing.price - effective})` : "";
      toast.success(`BOUGHT · ${gemMeta(listing.gem)?.symbol} ${gemMeta(listing.gem)?.name}${discountNote}`);
      refresh();
    } catch (e) {
      // Rollback chips on failure
      addChips(effective);
      const msg = e?.response?.data?.detail || "Purchase failed";
      toast.error(msg.toUpperCase());
      refresh();
    } finally {
      setBusyId("");
    }
  };

  const cancelListing = async (listing) => {
    if (!email) return;
    setBusyId(listing.id);
    try {
      await axios.post(`${API}/gems/cancel`, { buyer_email: email, listing_id: listing.id });
      // Refund the gem locally
      addGem(listing.gem);
      sfx.click();
      toast.success(`WITHDRAWN · ${gemMeta(listing.gem)?.symbol} returned to your inventory`);
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Cancel failed";
      toast.error(msg.toUpperCase());
    } finally {
      setBusyId("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-black/95 border-white/10 text-white" data-testid="trade-hall-dialog">
        <DialogHeader>
          <DialogTitle className="text-white tracking-[0.4em] text-sm text-[#F0ABFC]">
            GEM TRADE HALL
          </DialogTitle>
        </DialogHeader>

        {topTrader && (
          <div className="border border-[#FFD400]/40 p-3 bg-[#FFD400]/5 mb-3">
            <div className="text-[10px] tracking-[0.4em] text-[#FFD400] mb-1">TOP TRADER · THIS WEEK</div>
            <div className="text-xs text-white/90 tracking-wider" data-testid="trade-top-trader">
              <b>{topTrader.handle}</b> <span className="text-white/50">·</span>{" "}
              <span className="text-[#39FF14] tabular-nums">{topTrader.chips}</span> chips traded across{" "}
              {topTrader.trades} sales
            </div>
          </div>
        )}

        {getHandleGem() === "prismatic" && (
          <div
            className="border border-[#F0ABFC]/40 p-2 bg-[#F0ABFC]/5 mb-3 text-[10px] tracking-[0.3em] prismatic-text text-center"
            data-testid="trade-prismatic-discount-hint"
          >
            ◈ PRISMATIC DISCOUNT · -{Math.round(PRISMATIC_DISCOUNT * 100)}% ON ALL LISTINGS
          </div>
        )}

        {/* LIST A GEM */}
        <div className="border border-white/10 p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] tracking-[0.4em] text-white/60">LIST YOUR GEM</div>
            <div className="flex gap-1" data-testid="sell-mode-toggle">
              <button
                onClick={() => setSellMode("listing")}
                data-testid="sell-mode-listing"
                className={`text-[9px] tracking-[0.3em] px-2 py-[2px] border transition ${sellMode === "listing" ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10" : "border-white/20 text-white/50 hover:text-white"}`}
              >
                FIXED
              </button>
              <button
                onClick={() => setSellMode("auction")}
                data-testid="sell-mode-auction"
                className={`text-[9px] tracking-[0.3em] px-2 py-[2px] border transition ${sellMode === "auction" ? "border-[#F0ABFC] text-[#F0ABFC] bg-[#F0ABFC]/10" : "border-white/20 text-white/50 hover:text-white"}`}
              >
                AUCTION 24H
              </button>
            </div>
          </div>
          {!email && (
            <div className="text-[11px] text-white/50 tracking-wider mb-2">
              Set a public handle first to trade.
            </div>
          )}
          <div className="flex flex-wrap gap-2 mb-2">
            {ALL_GEMS.map((g) => {
              const count = inventory[g.id] || 0;
              const selected = sellGem === g.id;
              const disabled = count === 0;
              return (
                <button
                  key={g.id}
                  disabled={disabled}
                  onClick={() => setSellGem(selected ? "" : g.id)}
                  data-testid={`trade-sell-gem-${g.id}`}
                  className={`text-[10px] tracking-[0.2em] px-2 py-1 border transition ${disabled ? "opacity-30 cursor-not-allowed" : ""} ${selected ? "" : "hover:brightness-125"}`}
                  style={{
                    borderColor: selected ? g.color : "rgba(255,255,255,0.15)",
                    color: g.color,
                    background: selected ? `${g.color}22` : "transparent",
                  }}
                >
                  {g.symbol} {g.name} <span className="text-white/60">×{count}</span>
                </button>
              );
            })}
          </div>
          <div className="flex gap-2 items-center">
            <Input
              type="number"
              min={10}
              max={5000}
              value={sellPrice}
              onChange={(e) => setSellPrice(e.target.value)}
              className="w-32 bg-black/50 border-white/20 text-white text-xs"
              placeholder="Price (chips)"
              data-testid="trade-sell-price-input"
              disabled={!sellGem}
            />
            <Button
              onClick={submitListing}
              disabled={!canList}
              data-testid="trade-submit-listing"
              className="text-[10px] tracking-[0.3em] bg-[#F0ABFC]/20 hover:bg-[#F0ABFC]/40 border border-[#F0ABFC] text-[#F0ABFC]"
            >
              {sellMode === "auction" ? "START 24H AUCTION" : "LIST FOR SALE"}
            </Button>
          </div>
        </div>

        {/* LISTINGS */}
        <div className="border border-white/10 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] tracking-[0.4em] text-white/60">OPEN LISTINGS</div>
            <div className="text-[10px] tracking-[0.3em] text-white/50">
              Balance:{" "}
              <span className="text-[#00F0FF] tabular-nums" data-testid="trade-chips-balance">
                {chips}
              </span>{" "}
              chips
            </div>
          </div>
          {loading ? (
            <div className="text-[10px] tracking-[0.3em] text-white/40 text-center py-4">LOADING…</div>
          ) : listings.length === 0 ? (
            <div className="text-[10px] tracking-[0.3em] text-white/40 text-center py-4" data-testid="trade-empty-state">
              NO LISTINGS YET · BE THE FIRST TO LIST
            </div>
          ) : (
            <ol className="space-y-2 max-h-72 overflow-y-auto">
              {listings.map((l) => {
                const meta = gemMeta(l.gem);
                const isMine = l.seller_handle && email && l.seller_handle.toLowerCase().includes(email.split("@")[0].toLowerCase());
                const busy = busyId === l.id;
                return (
                  <li
                    key={l.id}
                    className="flex items-center justify-between border-b border-white/5 pb-2 text-xs font-mono"
                    data-testid={`trade-listing-${l.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span style={{ color: meta?.color, textShadow: `0 0 8px ${meta?.color}88` }}>
                        {meta?.symbol} {meta?.name}
                      </span>
                      <span className="text-white/40 text-[10px]">by {l.seller_handle}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {(() => {
                        const eff = discountedPrice(l.price);
                        const discounted = eff < l.price;
                        return discounted ? (
                          <span className="tabular-nums font-bold flex items-center gap-1">
                            <span className="text-white/40 line-through text-[10px]" data-testid={`trade-original-${l.id}`}>{l.price}</span>
                            <span className="text-[#F0ABFC]" data-testid={`trade-discounted-${l.id}`}>{eff}</span>
                          </span>
                        ) : (
                          <span className="tabular-nums text-[#FFD400] font-bold">{l.price}</span>
                        );
                      })()}
                      {isMine ? (
                        <button
                          onClick={() => cancelListing(l)}
                          disabled={busy}
                          data-testid={`trade-cancel-${l.id}`}
                          className="text-[10px] tracking-[0.3em] px-2 py-0.5 border border-white/30 text-white/60 hover:text-white hover:border-white/60 transition"
                        >
                          WITHDRAW
                        </button>
                      ) : (
                        <button
                          onClick={() => buyListing(l)}
                          disabled={busy || chips < discountedPrice(l.price)}
                          data-testid={`trade-buy-${l.id}`}
                          className="text-[10px] tracking-[0.3em] px-2 py-0.5 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10 transition disabled:opacity-40"
                        >
                          {busy ? "..." : "BUY"}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* AUCTIONS */}
        <div className="border border-[#F0ABFC]/20 p-3 mt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] tracking-[0.4em] text-[#F0ABFC]">OPEN AUCTIONS · 24H</div>
            {gavelHolder && (
              <button
                data-testid="gavel-room-toggle"
                onClick={() => { sfx.click(); setGavelRoom((v) => !v); }}
                className={`text-[9px] tracking-[0.3em] px-2 py-[2px] border transition ${gavelRoom ? "border-[#FFD400] text-[#FFD400] bg-[#FFD400]/10" : "border-white/20 text-white/60 hover:text-white"}`}
                title="GAVEL-only: show auctions ≥ 1000 chips"
              >
                {gavelRoom ? "★ GAVEL ROOM" : "GAVEL ROOM"}
              </button>
            )}
          </div>
          {(() => {
            const shown = gavelRoom
              ? auctions.filter((a) => Math.max(a.start_bid || 0, a.top_bid || 0) >= 1000)
              : auctions;
            if (shown.length === 0) {
              return (
                <div className="text-[10px] tracking-[0.3em] text-white/40 text-center py-3" data-testid="auction-empty-state">
                  {gavelRoom ? "NO WHALE AUCTIONS · WAIT FOR ≥ 1000 CHIP LISTINGS" : "NO AUCTIONS YET · FLIP FROM THE HALL OF FAME"}
                </div>
              );
            }
            return (
              <ol className="space-y-2 max-h-52 overflow-y-auto">
                {shown.map((a) => {
                const meta = gemMeta(a.gem);
                const minBid = Math.max(a.start_bid, (a.top_bid || 0) + 1);
                const val = bidAmounts[a.id] ?? String(minBid);
                const bidNum = parseInt(val, 10) || 0;
                const busy = busyId === a.id;
                const mine = a.seller_handle && email && a.seller_handle.toLowerCase().includes(email.split("@")[0].toLowerCase());
                return (
                  <li key={a.id} className="border-b border-white/5 pb-2" data-testid={`auction-row-${a.id}`}>
                    <div className="flex items-center justify-between text-xs font-mono">
                      <div className="flex items-center gap-2 min-w-0">
                        <button
                          data-testid={`auction-watch-${a.id}`}
                          onClick={() => {
                            const r = toggleWatch(a.id);
                            if (!r.ok) { toast.error(String(r.reason || "WATCH FAILED").toUpperCase()); return; }
                            sfx.click();
                            if (r.added) toast.success(`WATCHED · reminder ~15m before close (${watchedCount()}/${maxWatch()})`);
                            else toast.info("UNWATCHED");
                            setBidAmounts((s) => ({ ...s })); // trigger re-render
                          }}
                          className={`text-[13px] leading-none transition ${isWatched(a.id) ? "text-[#FFD400]" : "text-white/30 hover:text-white/70"}`}
                          title={isWatched(a.id) ? "Unwatch this auction" : "Watch — you'll get a reminder ~15 min before it closes"}
                        >
                          {isWatched(a.id) ? "★" : "☆"}
                        </button>
                        <span style={{ color: meta?.color, textShadow: `0 0 8px ${meta?.color}88` }}>
                          {meta?.symbol} {meta?.name}
                        </span>
                        <span className="text-white/40 text-[10px] truncate">by {a.seller_handle}</span>
                      </div>
                      <div className="text-[10px] tracking-[0.2em] text-white/50">
                        top:{" "}
                        <span className="text-[#39FF14] tabular-nums" data-testid={`auction-top-${a.id}`}>
                          {a.top_bid || a.start_bid}
                        </span>
                      </div>
                    </div>
                    {!mine && (
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          min={minBid}
                          value={val}
                          onChange={(e) => setBidAmounts((s) => ({ ...s, [a.id]: e.target.value }))}
                          className="h-7 w-24 bg-black/40 border-white/20 text-white text-[10px]"
                          data-testid={`auction-bid-input-${a.id}`}
                        />
                        <button
                          disabled={busy || bidNum < minBid || chips < bidNum}
                          onClick={async () => {
                            setBusyId(a.id);
                            const spend = spendChips(bidNum);
                            if (!spend.ok) { toast.error("Not enough chips"); setBusyId(""); return; }
                            try {
                              const r = await axios.post(`${API}/gems/auctions/bid`, { bidder_email: email, auction_id: a.id, amount: bidNum });
                              sfx.click();
                              if (r.data?.sniped) {
                                toast.success(`SNIPED! · ${bidNum} on ${meta?.symbol} at the wire`, { duration: 5000 });
                              } else {
                                toast.success(`BID PLACED · ${bidNum} on ${meta?.symbol}`);
                              }
                              refresh();
                            } catch (e) {
                              addChips(bidNum);
                              toast.error(String(e?.response?.data?.detail || "BID FAILED").toUpperCase());
                            } finally { setBusyId(""); }
                          }}
                          data-testid={`auction-bid-${a.id}`}
                          className="text-[10px] tracking-[0.3em] px-2 py-[3px] border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC]/10 disabled:opacity-40 transition"
                        >
                          {busy ? "..." : "BID"}
                        </button>
                        <span className="text-[9px] text-white/40 ml-auto">
                          min {minBid}
                        </span>
                      </div>
                    )}
                    {/* Bidbot ceiling — auto-rebids up to this cap */}
                    {!mine && email && (
                      <div className="flex items-center gap-2 mt-1 text-[9px] tracking-[0.2em] text-white/50">
                        <span>BIDBOT ≤</span>
                        <Input
                          type="number"
                          min={0}
                          defaultValue={getBidbotCeiling(a.id) || ""}
                          onBlur={(e) => {
                            setBidbotCeiling(a.id, parseInt(e.target.value, 10) || 0);
                            toast.success(`BIDBOT · ceiling set to ${parseInt(e.target.value, 10) || 0} chips`);
                          }}
                          data-testid={`auction-bidbot-${a.id}`}
                          placeholder="0 = off"
                          className="h-6 w-20 bg-black/40 border-white/10 text-white/70 text-[10px]"
                        />
                      </div>
                    )}
                    {/* Reactions bar */}
                    {email && (
                      <div className="flex items-center gap-2 mt-1" data-testid={`auction-reactions-${a.id}`}>
                        {["fire", "eyes", "trophy"].map((em) => {
                          const glyphs = { fire: "▲", eyes: "◉", trophy: "♛" };
                          const n = (reactions[a.id] || {})[em] || 0;
                          return (
                            <button
                              key={em}
                              disabled={reactBusy}
                              onClick={async () => {
                                setReactBusy(true);
                                try {
                                  await axios.post(`${API}/gems/auctions/react`, { email, auction_id: a.id, emoji: em });
                                  const rr = await axios.get(`${API}/gems/auctions/reactions?auction_ids=${a.id}`);
                                  setReactions((s) => ({ ...s, [a.id]: (rr.data?.reactions || {})[a.id] || s[a.id] }));
                                } catch {}
                                finally { setReactBusy(false); }
                              }}
                              data-testid={`auction-react-${em}-${a.id}`}
                              className="text-[10px] tracking-[0.2em] px-1.5 py-[1px] border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition"
                            >
                              {glyphs[em]} {n}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </li>
                );
              })}
              </ol>
            );
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
